import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

// Minha Empresa → NFS-e → "Retenção inteligente do ISSQN".
// Liga/desliga, mês de referência da alíquota efetiva do Simples, regras
// municipais complementares (com base legal) e as últimas análises (auditoria).
// Sempre a empresa do login (GET/PUT /fiscal/nfse/...).

const REGRA_VAZIA = {
  codigo_municipio: '', codigo_servico: '', regime_prestador: '', categoria_tomador: '', tomador_cpf_cnpj: '',
  retem: true, base_legal: '', vigencia_inicio: '', vigencia_fim: '', situacao: 'pendente', verificado_em: '', observacoes: '',
};
const SITUACAO_COR = { validada: '#166534', pendente: '#92400e', expirada: '#6b7280' };
const fs = { fontSize: 12 };
const inp = { padding: '3px 6px', border: '1px solid #ccc', borderRadius: 3, fontSize: 12 };

export default function ConfigRetencaoIss({ codigoMunicipioOrg }) {
  const [cfg, setCfg] = useState(null);
  const [regras, setRegras] = useState([]);
  const [analises, setAnalises] = useState([]);
  const [editando, setEditando] = useState(null);
  const [msg, setMsg] = useState('');

  const carregar = useCallback(() => {
    api.get('/fiscal/nfse/retencao-config').then(r => setCfg(r.data)).catch(e => setMsg(e.response?.data?.erro || 'Erro ao carregar.'));
    api.get('/fiscal/nfse/regras-retencao').then(r => setRegras(r.data)).catch(() => {});
    api.get('/fiscal/nfse/analises-iss').then(r => setAnalises(r.data)).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const salvarCfg = async (novo) => {
    setMsg('');
    try {
      const { data } = await api.put('/fiscal/nfse/retencao-config', { ativo: novo.ativo, aliquota_competencia: novo.aliquota_competencia || null });
      setCfg(data); setMsg('✔ Configuração salva.');
    } catch (e) { setMsg(e.response?.data?.erro || 'Erro ao salvar.'); }
  };

  const salvarRegra = async () => {
    setMsg('');
    try {
      if (editando.id) await api.put(`/fiscal/nfse/regras-retencao/${editando.id}`, editando);
      else await api.post('/fiscal/nfse/regras-retencao', editando);
      setEditando(null); carregar();
    } catch (e) { setMsg(e.response?.data?.erro || 'Erro ao salvar a regra.'); }
  };
  const setR = (k, v) => setEditando(r => ({ ...r, [k]: v }));

  if (!cfg) return <div style={{ padding: 10, ...fs }}>{msg || 'Carregando…'}</div>;

  return (
    <div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, fontWeight: 600 }}>
        <input type="checkbox" checked={!!cfg.ativo} onChange={e => salvarCfg({ ...cfg, ativo: e.target.checked })} />
        Ligar a retenção inteligente do ISSQN
      </label>
      <p style={{ ...fs, color: '#666', margin: '4px 0 10px' }}>
        Antes de cada NFS-e o sistema consulta os parâmetros oficiais do município (Sistema Nacional) e as regras
        abaixo e mostra a seção "Tributação Municipal — ISSQN". Quando não dá para confirmar a retenção, a emissão
        pede decisão de administrador/gerente com justificativa (fica registrada).
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', ...fs }}>
        Mês de referência da alíquota efetiva do Simples (campo acima):
        <input type="month" style={inp} value={cfg.aliquota_competencia || ''}
          onChange={e => setCfg(c => ({ ...c, aliquota_competencia: e.target.value }))} />
        <button type="button" style={inp} onClick={() => salvarCfg(cfg)}>Salvar</button>
        <span>Alíquota atual: <strong>{cfg.aliquota != null ? `${String(cfg.aliquota).replace('.', ',')}%` : 'não cadastrada'}</strong></span>
      </div>
      {msg && <div style={{ ...fs, marginTop: 6, color: msg.startsWith('✔') ? '#166534' : '#b91c1c' }}>{msg}</div>}

      <div style={{ marginTop: 14, fontWeight: 700, fontSize: 13 }}>Regras municipais complementares</div>
      <p style={{ ...fs, color: '#666', margin: '2px 0 6px' }}>
        Só regras <strong>validadas</strong>, com base legal verificável, decidem. Uma regra sem tomador e sem
        categoria vale para todos os tomadores; com categoria, serve de referência na decisão fiscal.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...fs }}>
        <thead><tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
          <th>Município</th><th>Serviço</th><th>Tomador / categoria</th><th>Retém</th><th>Base legal</th><th>Vigência</th><th>Situação</th><th />
        </tr></thead>
        <tbody>
          {regras.map(r => (
            <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
              <td>{r.codigo_municipio}</td>
              <td>{r.codigo_servico || 'todos'}</td>
              <td>{r.tomador_cpf_cnpj || r.categoria_tomador || 'todos'}</td>
              <td>{r.retem ? 'Sim' : 'Não'}</td>
              <td style={{ maxWidth: 260 }}>{r.base_legal}</td>
              <td>{r.vigencia_inicio}{r.vigencia_fim ? ` a ${r.vigencia_fim}` : ''}</td>
              <td style={{ color: SITUACAO_COR[r.situacao], fontWeight: 600 }}>{r.situacao}{r.origem === 'decisao_fiscal' ? ' (decisão)' : ''}</td>
              <td><button type="button" style={inp} onClick={() => setEditando({ ...REGRA_VAZIA, ...r })}>Editar</button></td>
            </tr>
          ))}
          {!regras.length && <tr><td colSpan={8} style={{ color: '#888', padding: 6 }}>Nenhuma regra cadastrada.</td></tr>}
        </tbody>
      </table>
      {!editando && (
        <button type="button" style={{ ...inp, marginTop: 6 }}
          onClick={() => setEditando({ ...REGRA_VAZIA, codigo_municipio: codigoMunicipioOrg || '' })}>+ Nova regra</button>
      )}
      {editando && (
        <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 8, marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6, ...fs }}>
          <label>Município (IBGE)<br /><input style={inp} value={editando.codigo_municipio} onChange={e => setR('codigo_municipio', e.target.value)} /></label>
          <label>Serviço (cTribNac ou subitem, vazio = todos)<br /><input style={inp} value={editando.codigo_servico || ''} onChange={e => setR('codigo_servico', e.target.value)} placeholder="ex.: 14.06" /></label>
          <label>Regime do prestador<br />
            <select style={inp} value={editando.regime_prestador || ''} onChange={e => setR('regime_prestador', e.target.value)}>
              <option value="">Todos</option><option value="simples">Simples Nacional</option><option value="mei">MEI</option><option value="normal">Regime normal</option>
            </select></label>
          <label>CPF/CNPJ do tomador (vazio = categoria)<br /><input style={inp} value={editando.tomador_cpf_cnpj || ''} onChange={e => setR('tomador_cpf_cnpj', e.target.value)} /></label>
          <label>Categoria do tomador<br /><input style={inp} value={editando.categoria_tomador || ''} onChange={e => setR('categoria_tomador', e.target.value)} placeholder="ex.: órgão público" /></label>
          <label>Retém o ISS?<br />
            <select style={inp} value={editando.retem ? '1' : '0'} onChange={e => setR('retem', e.target.value === '1')}>
              <option value="1">Sim — tomador retém</option><option value="0">Não</option>
            </select></label>
          <label style={{ gridColumn: '1 / -1' }}>Base legal (obrigatória)<br /><input style={{ ...inp, width: '100%' }} value={editando.base_legal} onChange={e => setR('base_legal', e.target.value)} placeholder="ex.: LC 05/2022 de Imperatriz, art. 120, I" /></label>
          <label>Início da vigência<br /><input type="date" style={inp} value={editando.vigencia_inicio || ''} onChange={e => setR('vigencia_inicio', e.target.value)} /></label>
          <label>Fim da vigência<br /><input type="date" style={inp} value={editando.vigencia_fim || ''} onChange={e => setR('vigencia_fim', e.target.value)} /></label>
          <label>Situação<br />
            <select style={inp} value={editando.situacao} onChange={e => setR('situacao', e.target.value)}>
              <option value="pendente">Pendente</option><option value="validada">Validada</option><option value="expirada">Expirada</option>
            </select></label>
          <label>Última verificação<br /><input type="date" style={inp} value={editando.verificado_em || ''} onChange={e => setR('verificado_em', e.target.value)} /></label>
          <label style={{ gridColumn: '1 / -1' }}>Observações<br /><input style={{ ...inp, width: '100%' }} value={editando.observacoes || ''} onChange={e => setR('observacoes', e.target.value)} /></label>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6 }}>
            <button type="button" style={inp} onClick={salvarRegra}>💾 Salvar regra</button>
            <button type="button" style={inp} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, fontWeight: 700, fontSize: 13 }}>Últimas análises (auditoria)</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', ...fs }}>
        <thead><tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
          <th>Data</th><th>Venda</th><th>NFS-e</th><th>Resultado</th><th>Alíquota</th><th>Origem</th><th>Decisão</th><th>Conferência</th>
        </tr></thead>
        <tbody>
          {analises.map(a => (
            <tr key={a.id} style={{ borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
              <td>{new Date(a.criado_em).toLocaleString('pt-BR')}</td>
              <td>{a.venda_id}</td>
              <td>{a.numero_nfse || '—'}</td>
              <td>{a.resultado} — {a.retencao_aplicada ? 'retido' : a.retencao_aplicada === false ? 'não retido' : 'a definir'}</td>
              <td>{a.aliquota != null ? `${String(a.aliquota).replace('.', ',')}%` : '—'}</td>
              <td style={{ maxWidth: 220 }}>{a.regra_origem}</td>
              <td style={{ maxWidth: 200 }}>{a.decisao_manual ? `${a.decisao_usuario_nome || ''}: ${a.justificativa}` : '—'}</td>
              <td style={{ maxWidth: 220 }}>
                {(a.divergencias || []).length
                  ? <span style={{ color: '#b91c1c' }}>{a.divergencias.map(d => d.msg).join('; ')}</span>
                  : a.valores_oficiais ? <span style={{ color: '#166534' }}>OK</span> : '—'}
                {a.financeiro && a.financeiro.vinculado === false && a.financeiro.motivo !== 'sem ISS retido' && (
                  <div style={{ color: '#92400e' }}>{a.financeiro.motivo}</div>
                )}
              </td>
            </tr>
          ))}
          {!analises.length && <tr><td colSpan={8} style={{ color: '#888', padding: 6 }}>Nenhuma análise ainda.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
